import orderIcon from '@/helpers/order-icon';
import {
  getAccessInformationValue,
  getGeocodableEntityId,
} from '@/services/geocodableAddressUtils';
import { groupByCoordinate } from '@/components/SchedulePage/ScheduleMap/helpers/coordinates';
import { isAllInspectorsSelected } from '@/components/SchedulePage/ScheduleMap/helpers/selection';
import { getInitials, getInspectorColor } from '@/components/SchedulePage/ScheduleMap/utils';

const hasCoordinates = coordinates => {
  const { latitude, longitude } = coordinates || {};
  if (latitude === null || latitude === undefined || longitude === null || longitude === undefined) {
    return false;
  }

  const latNum = Number(latitude);
  const lngNum = Number(longitude);

  if (!Number.isFinite(latNum) || !Number.isFinite(lngNum)) {
    return false;
  }

  if (latNum === 0 && lngNum === 0) return false;
  if (latNum < -90 || latNum > 90 || lngNum < -180 || lngNum > 180) return false;

  return true;
};

export const getScheduleMapPosition = (coordinatesById, id) => {
  const coordinates = coordinatesById[String(id)];

  if (!hasCoordinates(coordinates)) return null;

  return [Number(coordinates.latitude), Number(coordinates.longitude)];
};

const getOrderLabel = (order, intl) => {
  const fallback = intl?.formatMessage({ id: 'SCHEDULE_MAP.MARKERS.ORDER_FALLBACK' }) || 'Pedido';
  const code = order?.code;
  const identifier = order?.building_id || order?.identifier || order?.public_id || order?.id;

  if (code && identifier && String(code) !== String(identifier)) {
    return `${code} - ${identifier}`;
  }

  const label = code || identifier;
  return label ? String(label) : fallback;
};

const formatAddress = order => {
  const parts = [
    order?.street,
    order?.number,
    order?.neighborhood,
    order?.city && order?.state ? `${order.city}/${order.state}` : order?.city,
  ].filter(Boolean);

  return parts.length ? parts.join(', ') : null;
};

const getAccompaniedInspectionText = (order, intl) => {
  const accompanied = order?.accompanied_inspection ?? order?.details?.accompanied_inspection;

  if (!accompanied) return null;

  if (typeof accompanied === 'boolean') {
    return accompanied ? (intl?.formatMessage({ id: 'SCHEDULE_MAP.MARKERS.YES' }) || 'Sim') : null;
  }

  if (typeof accompanied === 'string') {
    const isFalseStr = ['false', 'nao', 'não', '0'].includes(accompanied.trim().toLowerCase());
    return isFalseStr ? null : accompanied;
  }

  if (typeof accompanied === 'object') {
    const isAccompanied = accompanied.isAccompanied ?? accompanied.is_accompanied;

    if (isAccompanied === false) {
      return null;
    }

    const name = (accompanied.companionName || accompanied.companion_name || accompanied.name || '').trim();
    const contact = (accompanied.companionContact || accompanied.companion_contact || accompanied.phone || '').trim();

    if (name && contact) return `${name} (${contact})`;
    if (name) return name;
    if (contact) return contact;

    if (isAccompanied === true) {
      return intl?.formatMessage({ id: 'SCHEDULE_MAP.MARKERS.YES' }) || 'Sim';
    }
  }

  return null;
};

const getAllowedAtDateText = order => {
  const allowed = order?.allowed_at_date ?? order?.details?.allowed_at_date;

  if (!allowed) return null;

  if (typeof allowed === 'string') {
    const trimmed = allowed.trim();
    return trimmed ? trimmed : null;
  }

  if (typeof allowed === 'object') {
    const val = allowed.date || allowed.time || (allowed.allowed_at ? String(allowed.allowed_at) : null);
    return val ? String(val).trim() : null;
  }

  return String(allowed);
};

const hasAccompaniedSchedule = order => {
  const accompanied =
    order?.accompanied_inspection ??
    order?.accompaniedInspection ??
    order?.details?.accompanied_inspection ??
    order?.details?.accompaniedInspection;

  if (!accompanied) return false;

  if (typeof accompanied === 'boolean') return accompanied;

  if (typeof accompanied === 'string') {
    const normalized = accompanied.trim().toLowerCase();
    return !['', 'false', 'nao', 'não', '0'].includes(normalized);
  }

  if (typeof accompanied === 'object') {
    const isAccompanied = accompanied.isAccompanied ?? accompanied.is_accompanied;
    const scheduleDate = accompanied.scheduleDate ?? accompanied.schedule_date;

    if (isAccompanied === true) return true;
    if (typeof scheduleDate === 'string' && scheduleDate.trim()) return true;
  }

  return false;
};

const getPreScheduleText = order => {
  const accompanied =
    order?.accompanied_inspection ??
    order?.accompaniedInspection ??
    order?.details?.accompanied_inspection ??
    order?.details?.accompaniedInspection;

  if (!accompanied || typeof accompanied !== 'object') return null;

  const scheduleDate = accompanied.scheduleDate ?? accompanied.schedule_date;

  if (typeof scheduleDate === 'string') {
    const trimmed = scheduleDate.trim();
    return trimmed || null;
  }

  return null;
};

export const buildScheduleMapMarkers = ({
  coordinatesById,
  scheduledOrders,
  pendingOrders,
  inspectors,
  layers,
  selectedInspectorIds = [],
  intl,
}) => {
  const markersMap = new Map();
  const polylines = [];
  const keyTransactionsRaw = [];

  const inspectorsById = new Map(inspectors.map(inspector => [String(inspector.public_id), inspector]));
  const allInspectorsSelected = isAllInspectorsSelected(selectedInspectorIds, inspectors);

  const defaultInspectorFallback = intl?.formatMessage({ id: 'SCHEDULE_MAP.MARKERS.INSPECTOR_FALLBACK' }) || 'Vistoriador';
  const flexibleText = intl?.formatMessage({ id: 'SCHEDULE_MAP.FILTER.SCHEDULE_FLEXIBLE' }) || 'Flexível';
  const fixedText = intl?.formatMessage({ id: 'SCHEDULE_MAP.FILTER.SCHEDULE_FIXED' }) || 'Horário marcado';
  const defaultPickupLocation = intl?.formatMessage({ id: 'SCHEDULE_MAP.MARKERS.KEY_PICKUP_LOCATION' }) || 'Retirada de Chaves';
  const defaultReturnLocation = intl?.formatMessage({ id: 'SCHEDULE_MAP.MARKERS.KEY_RETURN_LOCATION' }) || 'Devolução de Chaves';
  const defaultWaitingSchedule = intl?.formatMessage({ id: 'SCHEDULE_MAP.MARKERS.WAITING_SCHEDULE' }) || 'Aguardando agendamento';
  const defaultBaseTitle = intl?.formatMessage({ id: 'SCHEDULE_MAP.MARKERS.INSPECTOR_BASE_TITLE' }) || 'Residência do Vistoriador';
  const defaultBaseDetail = intl?.formatMessage({ id: 'SCHEDULE_MAP.MARKERS.INSPECTOR_BASE_DETAIL' }) || 'Endereço residencial do vistoriador';

  scheduledOrders.forEach(item => {
    const order = item?.order ?? item;
    const orderId = getGeocodableEntityId(order);
    const inspectorId = item?.schedule?.who?.inspector_id;

    if (!allInspectorsSelected && !selectedInspectorIds.includes(String(inspectorId))) {
      return;
    }

    const inspector = inspectorsById.get(String(inspectorId));
    const assignedInspectorName = inspector?.name || item?.schedule?.who?.name;
    const inspectorColor = getInspectorColor(inspectorId, inspectors);
    const inspectorInitials = assignedInspectorName ? getInitials(assignedInspectorName) : null;
    const orderPosition = getScheduleMapPosition(coordinatesById, orderId);

    const startTime = item?.schedule?.when?.start_time;
    const scheduleTypeStr = item?.schedule?.flextime ? flexibleText : fixedText;
    const scheduleInfo = startTime ? `${startTime} (${scheduleTypeStr})` : scheduleTypeStr;

    const isFlextime = Boolean(item?.schedule?.flextime || order?.flextime || order?.details?.flextime);
    const isAccompanied = hasAccompaniedSchedule(order);

    if (layers.scheduledOrders && orderId && orderPosition) {
      markersMap.set(`scheduledOrder:${orderId}`, {
        id: String(orderId),
        type: 'scheduledOrder',
        position: orderPosition,
        orderIcon: orderIcon(order?.type),
        scheduleType: isFlextime ? 'flexible' : 'fixed',
        isFlextime,
        isAccompanied,
        inspectorInitials,
        inspectorColor,
        title: getOrderLabel(order, intl),
        address: formatAddress(order),
        scheduleInfo,
        inspectorName: assignedInspectorName,
        accompaniedInfo: getAccompaniedInspectionText(order, intl),
        liberatedInfo: getAllowedAtDateText(order),
        rawOrder: order,
      });
    }
  });

  const processKeysForOrder = (order, item = null) => {
    const orderId = getGeocodableEntityId(order);
    if (!orderId) return;

    const orderPosition = getScheduleMapPosition(coordinatesById, orderId);
    const accessInformation =
      order?.access_information ||
      order?.accessInformation ||
      order?.details?.access_information ||
      order?.details?.accessInformation;

    const inspectorId = item?.schedule?.who?.inspector_id;
    const inspector = inspectorId ? inspectorsById.get(String(inspectorId)) : null;
    const assignedInspectorName = inspector?.name || item?.schedule?.who?.name;
    const inspectorColor = inspectorId ? getInspectorColor(inspectorId, inspectors) : '#00c3e6';
    const inspectorInitials = assignedInspectorName ? getInitials(assignedInspectorName) : '';

    const pickupPosition = getScheduleMapPosition(coordinatesById, `${orderId}:keys-pickup`);
    const returnPosition = getScheduleMapPosition(coordinatesById, `${orderId}:keys-return`);

    if (pickupPosition && layers.keyPickups) {
      const locationName = accessInformation?.address?.street
        ? formatAddress(accessInformation.address)
        : getAccessInformationValue(accessInformation, 'keysLocation') || defaultPickupLocation;

      keyTransactionsRaw.push({
        action: 'pickup',
        orderId,
        orderLabel: getOrderLabel(order, intl),
        position: pickupPosition,
        inspectorName: assignedInspectorName || null,
        inspectorColor,
        inspectorInitials,
        locationName,
      });

      if (
        orderPosition &&
        layers.scheduledOrders &&
        (pickupPosition[0] !== orderPosition[0] || pickupPosition[1] !== orderPosition[1])
      ) {
        polylines.push({
          id: `pickup-line-${orderId}`,
          positions: [pickupPosition, orderPosition],
          color: inspectorColor,
        });
      }
    }

    if (returnPosition && layers.keyReturns) {
      const locationName = accessInformation?.keysDevolutionAddress?.street
        ? formatAddress(accessInformation.keysDevolutionAddress)
        : getAccessInformationValue(accessInformation, 'keysDevolutionLocation') || defaultReturnLocation;

      keyTransactionsRaw.push({
        action: 'return',
        orderId,
        orderLabel: getOrderLabel(order, intl),
        position: returnPosition,
        inspectorName: assignedInspectorName || null,
        inspectorColor,
        inspectorInitials,
        locationName,
      });

      if (
        orderPosition &&
        layers.scheduledOrders &&
        (returnPosition[0] !== orderPosition[0] || returnPosition[1] !== orderPosition[1])
      ) {
        polylines.push({
          id: `return-line-${orderId}`,
          positions: [orderPosition, returnPosition],
          color: inspectorColor,
        });
      }
    }
  };

  scheduledOrders.forEach(item => {
    const order = item?.order ?? item;
    const inspectorId = item?.schedule?.who?.inspector_id;

    if (allInspectorsSelected || selectedInspectorIds.includes(String(inspectorId))) {
      processKeysForOrder(order, item);
    }
  });

  if (layers.waitingOrders) {
    pendingOrders.forEach(order => {
      processKeysForOrder(order, null);
    });
  }

  if (layers.realEstates || layers.keyPickups || layers.keyReturns) {
    const keyGroupsByCoord = groupByCoordinate(keyTransactionsRaw, item => item.position, 5);

    keyGroupsByCoord.forEach((groupItems, coordKey) => {
      const position = groupItems[0].position;
      const pickupCount = groupItems.filter(i => i.action === 'pickup').length;
      const returnCount = groupItems.filter(i => i.action === 'return').length;

      markersMap.set(`keysGroup:${coordKey}`, {
        id: `keysGroup:${coordKey}`,
        type: 'keysGroup',
        position,
        pickupCount,
        returnCount,
        locationName: groupItems[0].locationName,
        items: groupItems,
        title: groupItems[0].locationName,
      });
    });
  }

  if (layers.waitingOrders) {
    pendingOrders.forEach(item => {
      const order = item?.order ?? item;
      const orderId = getGeocodableEntityId(order);

      if (!orderId) return;

      const orderPosition = getScheduleMapPosition(coordinatesById, orderId);

      const isFlextime = Boolean(item?.schedule?.flextime || order?.flextime || order?.details?.flextime);
      const isAccompanied = hasAccompaniedSchedule(order);

      if (orderPosition) {
        markersMap.set(`waitingOrder:${orderId}`, {
          id: String(orderId),
          type: 'waitingOrder',
          position: orderPosition,
          orderIcon: orderIcon(order?.type),
          isFlextime,
          isAccompanied,
          title: getOrderLabel(order, intl),
          address: formatAddress(order),
          scheduleInfo: defaultWaitingSchedule,
          preScheduleInfo: getPreScheduleText(order),
          accompaniedInfo: getAccompaniedInspectionText(order, intl),
          liberatedInfo: getAllowedAtDateText(order),
          rawOrder: order,
        });
      }
    });
  }

  if (layers.inspectorBases) {
    inspectors.forEach(inspector => {
      const inspectorId = getGeocodableEntityId(inspector);

      if (!inspectorId) return;

      if (!allInspectorsSelected && !selectedInspectorIds.includes(String(inspector.public_id))) {
        return;
      }

      const basePosition = getScheduleMapPosition(coordinatesById, `${inspectorId}:base`);
      const inspectorColor = getInspectorColor(inspectorId, inspectors);

      if (basePosition) {
        markersMap.set(`inspectorBase:${inspectorId}`, {
          id: `${inspectorId}:base`,
          type: 'inspectorBase',
          position: basePosition,
          inspectorInitials: getInitials(inspector.name),
          inspectorColor,
          title: defaultBaseTitle,
          inspectorName: inspector.name,
          detail: defaultBaseDetail,
        });
      }
    });
  }

  const propertyMarkers = Array.from(markersMap.values()).filter(m =>
    ['scheduledOrder', 'waitingOrder'].includes(m.type)
  );

  const uniquePropertyMarkersByOrderId = new Map();

  propertyMarkers.forEach(marker => {
    const orderId = String(marker.id || '');

    if (!orderId) return;

    const existingMarker = uniquePropertyMarkersByOrderId.get(orderId);

    if (!existingMarker || marker.type === 'scheduledOrder') {
      uniquePropertyMarkersByOrderId.set(orderId, marker);
    }
  });

  const groupsByCoord = groupByCoordinate(
    Array.from(uniquePropertyMarkersByOrderId.values()),
    marker => marker.position,
    5
  );

  groupsByCoord.forEach((items, coordKey) => {
    if (items.length > 1) {
      items.forEach(item => {
        markersMap.delete(`${item.type}:${item.id}`);
      });

      const position = items[0].position;
      const address = items.find(i => i.address)?.address || items[0].address || items[0].title;

      markersMap.set(`ordersGroup:${coordKey}`, {
        id: `ordersGroup:${coordKey}`,
        type: 'ordersGroup',
        position,
        count: items.length,
        address,
        items,
        title: address,
      });
    }
  });

  return {
    markers: Array.from(markersMap.values()),
    polylines,
  };
};
